import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { DollarSign, TrendingUp, Receipt, HandCoins } from 'lucide-react';
import { useI18n } from '@/i18n';
import { FinanceOverviewTab } from '@/features/finance/components/FinanceOverviewTab';
import { ExpensesTab } from '@/features/finance/components/ExpensesTab';
import { ContributionsTab } from '@/features/finance/components/ContributionsTab';

type TabType = 'overview' | 'expenses' | 'contributions';

export function FinancePage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const currentTab = (tab as TabType) || 'overview';

  const TABS: { id: TabType; label: string; icon: typeof DollarSign }[] = [
    { id: 'overview', label: t('financePage.tabs.overview'), icon: TrendingUp },
    { id: 'expenses', label: t('financePage.tabs.expenses'), icon: Receipt },
    { id: 'contributions', label: t('financePage.tabs.contributions'), icon: HandCoins },
  ];

  const handleTabChange = (next: TabType) =>
    navigate(next === 'overview' ? '/finance' : `/finance/${next}`);

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('financePage.pageTitle')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('financePage.pageDescription')}</p>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={clsx(
                'flex items-center gap-2 pb-3 border-b-2 transition-colors',
                currentTab === id
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {currentTab === 'overview' && <FinanceOverviewTab />}
      {currentTab === 'expenses' && <ExpensesTab />}
      {currentTab === 'contributions' && <ContributionsTab />}
    </div>
  );
}
